import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BoardStats } from 'src/app/core/models/boardStats.model';
import { CollectionSummaryDTO } from 'src/app/core/models/collectionSummaryDTO.model';
import { InternalInventory } from 'src/app/core/models/internal-inventory.model';
import { LeadDTO } from 'src/app/core/models/lead.model';
import { Location } from 'src/app/core/models/location.model';
import { SalesRep } from 'src/app/core/models/sales-rep.model';
import { BoardStatService } from 'src/app/core/services/boardstats.service';
import { CollectionService } from 'src/app/core/services/collection.service';
import { InternalInventoryService } from 'src/app/core/services/internal-inventory.service';
import { LeadService } from 'src/app/core/services/lead.service';
import { LocationService } from 'src/app/core/services/location.service';
import { LoginService } from 'src/app/core/services/login.service';
import { SalesRepService } from 'src/app/core/services/sales-rep.service';
import { UserType } from 'src/app/core/models/userType.models';
import { firstValueFrom } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BoardStatsByIdComponent } from 'src/app/pages/boardStatsById/boardStatsById.component';
import { FusionConnectDailyStat } from 'src/app/core/models/fusion-connect.model';
import { FusionConnectService } from 'src/app/core/services/fusion-connect.service';

type InternalView = 'overview' | 'sales' | 'lease' | 'operations' | 'leads';
type QuickRange = 'yesterday' | 'last7' | 'last30' | 'last3m' | 'last12m' | 'ytd' | 'all';
type InternalBoardStat = BoardStats & {
  __locationName?: string;
  __salesRepName?: string;
  __state?: string;
  __isWarehouse?: boolean;
};

@Component({
  selector: 'app-internal-dashboard',
  template: `
<div class="container-fluid internal-dashboard">
  <div class="dashboard-hero">
    <div class="dashboard-brand">
      <img class="dashboard-logo" src="assets/images/logo-dark.png" alt="Fortune Box">
      <div>
        <div class="eyebrow">Fortune Backoffice</div>
        <h2 class="mb-1">{{ getViewTitle() }}</h2>
        <div class="text-muted">Track sales performance, live Fortune activity, machines, and operations in one place.</div>
      </div>
    </div>
    <button *ngIf="view !== 'overview'" class="btn btn-outline-secondary" (click)="back()">
      <i class="ri-arrow-left-line me-1"></i> Back to Dashboard
    </button>
  </div>

  <ng-template #periodToolbar>
    <div class="period-toolbar mb-4" [class.expanded]="dateFilterExpanded">
      <button
        type="button"
        class="period-toggle"
        [attr.aria-expanded]="dateFilterExpanded"
        (click)="dateFilterExpanded = !dateFilterExpanded">
        <span><i class="ri-calendar-line"></i> Date filter</span>
        <small>{{ activeRangeLabel }}</small>
        <i class="ri-arrow-down-s-line period-toggle-icon" [class.expanded]="dateFilterExpanded"></i>
      </button>

      <div class="period-body" *ngIf="dateFilterExpanded">
        <div class="period-row">
          <span class="period-label">Years</span>
          <button *ngFor="let year of availableYears" type="button" class="period-chip" [class.active]="isYearSelected(year)" (click)="toggleYear(year)">
            {{ year }}
          </button>
        </div>

        <div class="period-row">
          <span class="period-label">Months</span>
          <button type="button" class="period-chip" [class.active]="isQuarterSelected(1)" (click)="toggleQuarter(1)">Q1</button>
          <button type="button" class="period-chip" [class.active]="isQuarterSelected(2)" (click)="toggleQuarter(2)">Q2</button>
          <button type="button" class="period-chip" [class.active]="isQuarterSelected(3)" (click)="toggleQuarter(3)">Q3</button>
          <button type="button" class="period-chip" [class.active]="isQuarterSelected(4)" (click)="toggleQuarter(4)">Q4</button>
          <button *ngFor="let month of monthOptions" type="button" class="period-chip" [class.active]="isMonthSelected(month.value)" (click)="toggleMonth(month.value)">
            {{ month.label }}
          </button>
        </div>

        <div class="period-row">
          <span class="period-label">Specific</span>
          <input class="period-date" type="date" [value]="customStartInput" (change)="onCustomStartChange($event)">
          <input class="period-date" type="date" [value]="customEndInput" (change)="onCustomEndChange($event)">
          <button type="button" class="period-chip dark" (click)="applyCustomRange()">Apply</button>
          <button type="button" class="period-chip" (click)="setQuickRange('yesterday')">Yesterday</button>
          <button type="button" class="period-chip" (click)="setQuickRange('last7')">Last 7D</button>
          <button type="button" class="period-chip" (click)="setQuickRange('last30')">Last 30D</button>
          <button type="button" class="period-chip" (click)="setQuickRange('last3m')">Last 3M</button>
          <button type="button" class="period-chip" (click)="setQuickRange('last12m')">Last 12M</button>
          <button type="button" class="period-chip" (click)="setQuickRange('ytd')">YTD</button>
          <button type="button" class="period-chip" (click)="setQuickRange('all')">All</button>
          <button type="button" class="period-chip" (click)="resetPeriod()">Reset</button>
        </div>
      </div>
    </div>
  </ng-template>

  <div *ngIf="loading" class="card dashboard-panel">
    <div class="card-body">Loading dashboard...</div>
  </div>
  <div *ngIf="loadError" class="alert alert-warning">{{ loadError }}</div>
  <div *ngIf="!loadError && dataWarnings.length" class="alert alert-warning">
    <div *ngFor="let warning of dataWarnings">{{ warning }}</div>
  </div>

  <ng-container *ngIf="!loading">
    <div *ngIf="view === 'overview'" class="overview-grid">
      <button class="metric-card primary" (click)="open('sales')">
        <span>Fortune Sales</span>
        <strong>{{ currentMonthSalesTotal | currency:'USD':'symbol':'1.0-0' }}</strong>
        <small>{{ boardSalesCount }} boards &middot; {{ cabinetSalesCount }} cabinets sold in selected period</small>
      </button>

      <button class="metric-card" (click)="goToCollections()">
        <span>Fortune Route</span>
        <strong>{{ collectedNet | currency:'USD':'symbol':'1.2-2' }}</strong>
        <small>{{ collectedLocationCount }} locations collected net in selected period</small>
      </button>

      <button class="metric-card" (click)="open('lease')">
        <span>Fortune Lease</span>
        <strong>{{ leaseMonthlyRevenue | currency:'USD':'symbol':'1.0-0' }}</strong>
        <small>{{ leaseBoards }} lease boards at $5/$10 daily rates</small>
      </button>

      <button class="metric-card" (click)="open('operations')">
        <span>Cash In and Net Per Day</span>
        <div class="metric-pair">
          <div><em>Cash in/day</em><strong>{{ cashInPerDay | currency:'USD':'symbol':'1.2-2' }}</strong></div>
          <div><em>Net/day</em><strong>{{ netPerDay | currency:'USD':'symbol':'1.2-2' }}</strong></div>
        </div>
      </button>

      <button class="metric-card" [class.readonly]="!isRoot" (click)="goToInventory()">
        <span>Fortune Inventory</span>
        <div class="inventory-mini-table">
          <div></div>
          <b>GA</b>
          <b>TX</b>
          <small>Boards</small>
          <strong>{{ inventory.gaBoards || 0 }}</strong>
          <strong>{{ inventory.txBoards || 0 }}</strong>
          <small>Cabinets</small>
          <strong>{{ inventory.gaCabinets || 0 }}</strong>
          <strong>{{ inventory.txCabinets || 0 }}</strong>
          <small>Kiosk</small>
          <strong>{{ inventory.gaKiosks || 0 }}</strong>
          <strong>{{ inventory.txKiosks || 0 }}</strong>
        </div>
        <small class="field-machines"><b>{{ routeMachineCount | number:'1.0-0' }}</b> machines in the field</small>
      </button>

      <button class="metric-card leads-card" (click)="open('leads')">
        <span>Fortune Leads</span>
        <strong>{{ currentMonthLeads.length | number:'1.0-0' }}</strong>
        <small>Total leads in current month</small>
        <div class="recent-leads-preview" *ngIf="recentCurrentMonthLeads.length">
          <div *ngFor="let lead of recentCurrentMonthLeads; trackBy: trackByLead">
            <b>{{ lead.businessName }}</b>
            <small>{{ getLeadSalesRepName(lead.assignedSalesRepId) }}</small>
          </div>
          <small class="leads-more" *ngIf="currentMonthLeads.length > 2">
            +{{ currentMonthLeads.length - 2 }} more
          </small>
        </div>
      </button>
    </div>

    <div *ngIf="view === 'sales'" class="sales-detail-stack">
      <ng-container *ngTemplateOutlet="periodToolbar"></ng-container>

      <div class="card dashboard-panel">
        <div class="card-body">
          <h5>Board Sales</h5>
          <div class="table-responsive">
            <table class="table table-hover align-middle sales-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>State</th>
                  <th class="text-end">Boards</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Amount Sold</th>
                  <th>Install Date</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let loc of boardSaleLocations">
                  <td data-label="Location">{{ loc.locationName }}</td>
                  <td data-label="State">{{ getLocationState(loc) }}</td>
                  <td data-label="Boards" class="text-end">{{ loc.numBoards || 0 }}</td>
                  <td data-label="Unit Price" class="text-end">{{ getSalesOutletUnitPrice(loc) | currency:'USD':'symbol':'1.0-0' }}</td>
                  <td data-label="Amount Sold" class="text-end">{{ getSalesOutletRowTotal(loc) | currency:'USD':'symbol':'1.0-0' }}</td>
                  <td data-label="Install Date">{{ loc.locationStartDate | date:'mediumDate' }}</td>
                </tr>
                <tr *ngIf="boardSaleLocations.length === 0">
                  <td colspan="6" class="text-muted">No board sales for the selected period.</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="4" data-label="Total" class="text-end">Board Sales Total</th>
                  <th data-label="Amount Sold" class="text-end">{{ boardSalesTotal | currency:'USD':'symbol':'1.0-0' }}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div class="card dashboard-panel">
        <div class="card-body">
          <h5>Cabinet Sales</h5>
          <div class="table-responsive">
            <table class="table table-hover align-middle sales-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>State</th>
                  <th class="text-end">Cabinets</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Amount Sold</th>
                  <th>Install Date</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let loc of cabinetSaleLocations">
                  <td data-label="Location">{{ loc.locationName }}</td>
                  <td data-label="State">{{ getLocationState(loc) }}</td>
                  <td data-label="Cabinets" class="text-end">{{ loc.numCabinets || loc.numBoards || 0 }}</td>
                  <td data-label="Unit Price" class="text-end">{{ getSalesOutletUnitPrice(loc) | currency:'USD':'symbol':'1.0-0' }}</td>
                  <td data-label="Amount Sold" class="text-end">{{ getSalesOutletRowTotal(loc) | currency:'USD':'symbol':'1.0-0' }}</td>
                  <td data-label="Install Date">{{ loc.locationStartDate | date:'mediumDate' }}</td>
                </tr>
                <tr *ngIf="cabinetSaleLocations.length === 0">
                  <td colspan="6" class="text-muted">No cabinet sales for the selected period.</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th colspan="4" data-label="Total" class="text-end">Cabinet Sales Total</th>
                  <th data-label="Amount Sold" class="text-end">{{ cabinetSalesTotal | currency:'USD':'symbol':'1.0-0' }}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="view === 'lease'" class="card dashboard-panel">
      <div class="card-body">
        <h5>Lease Location Monthly Revenue</h5>
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Sales Rep</th>
                <th class="text-end">Boards</th>
                <th class="text-end">Daily Rate</th>
                <th class="text-end">Monthly Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let rep of leaseReps">
                <td>{{ rep.firstName }} {{ rep.lastName }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="link-button"
                    [class.active]="selectedLeaseSalesRepName === getSalesRepName(rep)"
                    (click)="openLeaseRepStats(rep)">
                    {{ rep.numBoards || 0 }}
                  </button>
                </td>
                <td class="text-end">{{ rep.tenDollarEnabled ? '$10' : '$5' }}</td>
                <td class="text-end">{{ ((rep.tenDollarEnabled ? 10 : 5) * (rep.numBoards || 0) * daysInCurrentMonth) | currency:'USD' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div *ngIf="view === 'lease' && selectedLeaseSalesRepName" class="card dashboard-panel lease-stats-panel">
      <div class="card-body">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-4">
          <div>
            <h5 class="mb-1">{{ selectedLeaseSalesRepName }} Board Stats</h5>
            <div class="text-muted">{{ activeRangeLabel }}</div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearLeaseRepStats()">Close</button>
        </div>

        <div class="stats-mobile-summary">
          <table class="table stats-summary-table">
            <thead>
              <tr>
                <th>Summary</th>
                <th class="text-end">Boards</th>
                <th class="text-end">Cash In</th>
                <th class="text-end">Net</th>
                <th class="text-end">Cash In/Day</th>
                <th class="text-end">Net/Day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Totals</td>
                <td class="text-end">{{ selectedLeaseRepStats.length }}</td>
                <td class="text-end">{{ selectedLeaseRepStats | sum:'cashInDiff' | number:'1.0-0' }}</td>
                <td class="text-end">{{ selectedLeaseRepStats | sum:'profit' | number:'1.0-0' }}</td>
                <td class="text-end">{{ selectedLeaseRepStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</td>
                <td class="text-end">{{ selectedLeaseRepStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-scroll-controls">
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(leaseStatsScroll, -1)">
            Left
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(leaseStatsScroll, 1)">
            Right
          </button>
        </div>

        <div class="table-responsive breakdown-table" #leaseStatsScroll>
          <table class="table table-bordered table-hover stats-table" style="min-width: 1200px;">
            <thead>
              <tr class="table-info totals-row">
                <th colspan="6"><strong>Totals ({{ selectedLeaseRepStats.length }} Boards)</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'cashInDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'cashOutDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'profit' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepAverageDays | number:'1.0-1' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ (selectedLeaseRepStats | average:'holdPercentage') * 100 | number:'1.0-2' }}%</strong></th>
                <th><strong>{{ percent((selectedLeaseRepStats | sum:'coinOutDiff'), (selectedLeaseRepStats | sum:'coinInDiff')) | number:'1.0-0' }}%</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'coinInDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'coinOutDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'gamePlayedDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ selectedLeaseRepStats | sum:'gameWonDiff' | number:'1.0-0' }}</strong></th>
                <th><strong>{{ ratio((selectedLeaseRepStats | sum:'coinInDiff'), (selectedLeaseRepStats | sum:'cashInDiff')) | number:'1.0-2' }}</strong></th>
                <th><strong>{{ ratio((selectedLeaseRepStats | sum:'coinInDiff'), (selectedLeaseRepStats | sum:'gamePlayedDiff')) | number:'1.0-2' }}</strong></th>
              </tr>
              <tr>
                <th (click)="sortData('timeStamp')">Read</th>
                <th (click)="sortData('assetTag')">Asset Tag</th>
                <th (click)="sortData('serialNumber')">Serial Number</th>
                <th (click)="sortData('location')">Location</th>
                <th (click)="sortData('state')">State</th>
                <th (click)="sortData('gameName')">Game Name</th>
                <th (click)="sortData('cashIn')">In</th>
                <th (click)="sortData('cashOut')">Out</th>
                <th (click)="sortData('netRevenue')">Net</th>
                <th (click)="sortData('daysSincePrevRead')">Days</th>
                <th (click)="sortData('cashIn/day')">In/Day</th>
                <th (click)="sortData('netRevenue/day')">Net/D</th>
                <th (click)="sortData('payoutPercentage')">Payout</th>
                <th (click)="sortData('rtp')">RPT</th>
                <th (click)="sortData('coinIn')">Turn</th>
                <th (click)="sortData('coinOut')">Credit</th>
                <th (click)="sortData('gamesPlayed')">Played</th>
                <th (click)="sortData('gamesWon')">Won</th>
                <th>Mult</th>
                <th>Bet</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of selectedLeaseRepStats; trackBy: trackByStat" class="stats-row">
                <td>{{ item.timeStamp | date:'MMM d, y' }}</td>
                <td>
                  <a href="javascript:void(0);" (click)="openBoardStatsModal(item.board?.assetTag)">
                    {{ item.board?.assetTag || '-' }}
                  </a>
                </td>
                <td>{{ item.board?.serialNumber || '-' }}</td>
                <td>{{ item.__locationName }}</td>
                <td>{{ item.__state }}</td>
                <td>{{ item.board?.currentDeployment?.installedGamePackage?.name || item.board?.installedGamePackage?.name }}</td>
                <td>{{ numberValue(item.cashInDiff).toFixed(0) }}</td>
                <td>{{ numberValue(item.cashOutDiff).toFixed(0) }}</td>
                <td>{{ numberValue(item.profit).toFixed(0) }}</td>
                <td>{{ item.daysSincePrevRead }}</td>
                <td>{{ perDay(item.cashInDiff, item.daysSincePrevRead).toFixed(0) }}</td>
                <td>{{ perDay(item.profit, item.daysSincePrevRead).toFixed(0) }}</td>
                <td>{{ numberValue(item.holdPercentage) * 100 | number:'1.0-2' }}%</td>
                <td>{{ percent(item.coinOutDiff, item.coinInDiff) | number:'1.0-0' }}%</td>
                <td>{{ numberValue(item.coinInDiff).toFixed(0) }}</td>
                <td>{{ numberValue(item.coinOutDiff).toFixed(0) }}</td>
                <td>{{ numberValue(item.gamePlayedDiff).toFixed(0) }}</td>
                <td>{{ numberValue(item.gameWonDiff).toFixed(0) }}</td>
                <td>{{ ratio(item.coinInDiff, item.cashInDiff).toFixed(2) }}</td>
                <td>{{ ratio(item.coinInDiff, item.gamePlayedDiff).toFixed(2) }}</td>
              </tr>
              <tr *ngIf="selectedLeaseRepStats.length === 0">
                <td colspan="20" class="text-center text-muted py-4">No board stats found for this sales rep in the selected period.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div *ngIf="view === 'leads'" class="card dashboard-panel">
      <div class="card-header">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <h5 class="mb-0">Lead Management</h5>
          <button (click)="openLeadModal()" class="btn btn-primary">+ Add Lead</button>
        </div>
      </div>

      <app-lead-modal
        *ngIf="showLeadModal"
        [lead]="selectedLead"
        (save)="saveLead($event)"
        (close)="showLeadModal = false; selectedLead = null">
      </app-lead-modal>

      <div class="card-body p-0">
        <div class="leads-table-container">
          <table class="table leads-table">
            <thead>
              <tr>
                <th>Business</th>
                <th>Status</th>
                <th>Sales Rep</th>
                <th>Interested</th>
                <th>Closed</th>
                <th>Expected Delivery</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let lead of currentMonthLeads; trackBy: trackByLead">
                <td>{{ lead.businessName }}</td>
                <td>
                  <span class="badge"
                    [ngClass]="{
                      'bg-secondary': lead.status === 'NEW' || lead.status === 'CLOSED',
                      'bg-warning': lead.status === 'CONTACTED',
                      'bg-info': lead.status === 'IN_PROGRESS',
                      'bg-success': lead.status === 'WON',
                      'bg-danger': lead.status === 'LOST'
                    }">
                    {{ lead.status }}
                  </span>
                </td>
                <td>{{ getLeadSalesRepName(lead.assignedSalesRepId) }}</td>
                <td>{{ lead.interestedMachineCount || 0 }}</td>
                <td>{{ lead.closedMachineCount || 0 }}</td>
                <td>{{ lead.expectedDeliveryDate | date }}</td>
                <td>
                  <button class="btn btn-sm btn-outline-secondary" (click)="editLead(lead)">Edit</button>
                  <button class="btn btn-sm btn-outline-danger" (click)="deleteLead(lead.id)">Delete</button>
                </td>
              </tr>
              <tr *ngIf="currentMonthLeads.length === 0">
                <td colspan="7" class="text-muted">No leads for the current month.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="leads-mobile-list">
          <div *ngFor="let lead of currentMonthLeads; trackBy: trackByLead" class="lead-mobile-card">
            <div class="lead-mobile-top">
              <strong>{{ lead.businessName }}</strong>
              <span class="badge"
                [ngClass]="{
                  'bg-secondary': lead.status === 'NEW' || lead.status === 'CLOSED',
                  'bg-warning': lead.status === 'CONTACTED',
                  'bg-info': lead.status === 'IN_PROGRESS',
                  'bg-success': lead.status === 'WON',
                  'bg-danger': lead.status === 'LOST'
                }">
                {{ lead.status }}
              </span>
            </div>
            <div class="lead-mobile-grid">
              <div><span>Sales Rep</span><b>{{ getLeadSalesRepName(lead.assignedSalesRepId) }}</b></div>
              <div><span>Interested</span><b>{{ lead.interestedMachineCount || 0 }}</b></div>
              <div><span>Closed</span><b>{{ lead.closedMachineCount || 0 }}</b></div>
              <div><span>Expected</span><b>{{ lead.expectedDeliveryDate | date }}</b></div>
            </div>
            <div class="lead-mobile-actions">
              <button class="btn btn-sm btn-outline-secondary" (click)="editLead(lead)">Edit</button>
              <button class="btn btn-sm btn-outline-danger" (click)="deleteLead(lead.id)">Delete</button>
            </div>
          </div>
          <div *ngIf="currentMonthLeads.length === 0" class="text-muted p-3">No leads for the current month.</div>
        </div>
      </div>
    </div>

    <ng-container *ngIf="view === 'operations'">
      <ng-container *ngTemplateOutlet="periodToolbar"></ng-container>
    </ng-container>

    <div *ngIf="view === 'operations'" class="row">
      <div class="col-12">
        <div class="card analytics-card table-card">
          <div class="card-body">
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-4">
              <h4 class="card-title mb-0">Board Stats Analysis</h4>
              <button type="button" class="btn btn-sm btn-outline-primary route-action-btn" (click)="toggleLiveStats()">
                {{ showLiveStats ? 'Hide Live Stats' : 'View Live Stats' }}
              </button>
            </div>
            <div class="stats-filter-bar">
              <label>
                <span>Game</span>
                <select class="form-select form-select-sm" [(ngModel)]="boardStatsGameFilter">
                  <option value="">All Games</option>
                  <option *ngFor="let game of boardStatsGameOptions" [value]="game">{{ game }}</option>
                </select>
              </label>
              <label>
                <span>Sales Rep</span>
                <select class="form-select form-select-sm" [(ngModel)]="boardStatsSalesRepFilter">
                  <option value="">All Sales Reps</option>
                  <option *ngFor="let rep of boardStatsSalesRepOptions" [value]="rep">{{ rep }}</option>
                </select>
              </label>
              <label>
                <span>State</span>
                <select class="form-select form-select-sm" [(ngModel)]="boardStatsStateFilter">
                  <option value="">All States</option>
                  <option *ngFor="let state of boardStatsStateOptions" [value]="state">{{ state || 'Unknown' }}</option>
                </select>
              </label>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearBoardStatsFilters()">Clear</button>
            </div>
            <div class="stats-mobile-summary">
              <table class="table stats-summary-table">
                <thead>
                  <tr>
                    <th>Summary</th>
                    <th class="text-end">Boards</th>
                    <th class="text-end">Cash In</th>
                    <th class="text-end">Net</th>
                    <th class="text-end">Cash In/Day</th>
                    <th class="text-end">Net/Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Totals</td>
                    <td class="text-end">{{ validStats.length }}</td>
                    <td class="text-end">{{ validStats | sum:'cashInDiff' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ validStats | sum:'profit' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ validStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ validStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="stats-mobile-sort">
              <label for="boardStatsSort">Sort by</label>
              <select id="boardStatsSort" class="form-select form-select-sm" [value]="boardStatsSortColumn" (change)="setBoardStatsSort($any($event.target).value)">
                <option *ngFor="let option of boardStatsSortOptions" [value]="option.value">{{ option.label }}</option>
              </select>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleBoardStatsSortDirection()">
                {{ boardStatsSortAscending ? 'Asc' : 'Desc' }}
              </button>
            </div>
            <div class="table-scroll-controls">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(boardStatsScroll, -1)">
                Left
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(boardStatsScroll, 1)">
                Right
              </button>
            </div>
            <div class="table-responsive breakdown-table" #boardStatsScroll>
              <table class="table table-bordered table-hover stats-table" style="min-width: 1200px;">
                <thead>
                  <tr class="table-info totals-row">
                    <th class="sticky" colspan="6"><strong>Totals ({{ validStats.length }} Boards)</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'cashInDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'cashOutDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'profit' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ daysSincePrevDisplay | number:'1.0-1' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ (validStats | average:'holdPercentage') * 100 | number:'1.0-2' }}%</strong></th>
                    <th class="sticky"><strong>{{ ((validStats | sum:'coinOutDiff') / (validStats | sum:'coinInDiff')) * 100 | number:'1.0-0' }}%</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'coinInDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'coinOutDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'gamePlayedDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ validStats | sum:'gameWonDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ (validStats | sum:'coinInDiff') / (validStats | sum:'cashInDiff') | number:'1.0-2' }}</strong></th>
                    <th class="sticky"><strong>{{ (validStats | sum:'coinInDiff') / (validStats | sum:'gamePlayedDiff') | number:'1.0-2' }}</strong></th>
                  </tr>
                  <tr>
                    <th (click)="sortData('timeStamp')">Read</th>
                    <th (click)="sortData('assetTag')">Asset Tag</th>
                    <th (click)="sortData('salesRep')">Sales Rep</th>
                    <th (click)="sortData('gameName')">Game Name</th>
                    <th (click)="sortData('location')">Location</th>
                    <th (click)="sortData('state')">State</th>
                    <th (click)="sortData('cashIn')">In</th>
                    <th (click)="sortData('cashOut')">Out</th>
                    <th (click)="sortData('netRevenue')">Net</th>
                    <th (click)="sortData('daysSincePrevRead')">Days</th>
                    <th (click)="sortData('cashIn/day')">In/Day</th>
                    <th (click)="sortData('netRevenue/day')">Net/D</th>
                    <th (click)="sortData('payoutPercentage')">Payout</th>
                    <th (click)="sortData('rtp')">RPT</th>
                    <th (click)="sortData('coinIn')">Turn</th>
                    <th (click)="sortData('coinOut')">Credit</th>
                    <th (click)="sortData('gamesPlayed')">Played</th>
                    <th (click)="sortData('gamesWon')">Won</th>
                    <th>Mult</th>
                    <th>Bet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of validStats; trackBy: trackByStat" class="stats-row">
                    <td>{{ item.timeStamp | date:'MMM d' }}</td>
                    <td>
                      <a href="javascript:void(0);" (click)="openBoardStatsModal(item.board?.assetTag)">
                        {{ item.board?.assetTag }}
                      </a>
                    </td>
                    <td>{{ item.__salesRepName }}</td>
                    <td>{{ item.board?.currentDeployment?.installedGamePackage?.name || item.board?.installedGamePackage?.name }}</td>
                    <td>{{ item.__locationName }}</td>
                    <td>{{ item.__state }}</td>
                    <td>{{ numberValue(item.cashInDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.cashOutDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.profit).toFixed(0) }}</td>
                    <td>{{ item.daysSincePrevRead }}</td>
                    <td>{{ perDay(item.cashInDiff, item.daysSincePrevRead).toFixed(0) }}</td>
                    <td>{{ perDay(item.profit, item.daysSincePrevRead).toFixed(0) }}</td>
                    <td>{{ numberValue(item.holdPercentage) * 100 | number:'1.0-2' }}%</td>
                    <td>{{ percent(item.coinOutDiff, item.coinInDiff) | number:'1.0-0' }}%</td>
                    <td>{{ numberValue(item.coinInDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.coinOutDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gamePlayedDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gameWonDiff).toFixed(0) }}</td>
                    <td>{{ ratio(item.coinInDiff, item.cashInDiff).toFixed(2) }}</td>
                    <td>{{ ratio(item.coinInDiff, item.gamePlayedDiff).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" *ngIf="view === 'operations' && showLiveStats" id="internal-live-stats">
      <div class="col-12">
        <div class="card analytics-card table-card">
          <div class="card-body">
            <h4 class="card-title mb-4">Field Live Stats</h4>
            <div class="table-scroll-controls">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(liveStatsScroll, -1)">
                Left
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(liveStatsScroll, 1)">
                Right
              </button>
            </div>
            <div class="table-responsive breakdown-table live-stats-scroll" #liveStatsScroll>
              <table class="table table-bordered table-hover stats-table live-stats-table" style="min-width: 1200px;">
                <thead>
                  <tr class="table-info totals-row">
                    <th colspan="6"><strong>Totals ({{ filteredFortuneLiveStats.length }} Rows)</strong></th>
                    <th><strong>{{ liveStatSum('cashIn') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatSum('cashOut') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatSum('net') | number:'1.0-0' }}</strong></th>
                    <th><strong>1.0</strong></th>
                    <th><strong>{{ liveStatAverage('cashIn') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatAverage('net') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ percent(liveStatSum('cashOut'), liveStatSum('cashIn')) | number:'1.0-2' }}%</strong></th>
                    <th><strong>{{ percent(liveStatSum('coinOut'), liveStatSum('coinIn')) | number:'1.0-0' }}%</strong></th>
                    <th><strong>{{ liveStatSum('coinIn') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatSum('coinOut') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatSum('gamesPlayed') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ liveStatSum('gamesWon') | number:'1.0-0' }}</strong></th>
                    <th><strong>{{ ratio(liveStatSum('coinIn'), liveStatSum('cashIn')) | number:'1.0-2' }}</strong></th>
                    <th><strong>{{ ratio(liveStatSum('coinIn'), liveStatSum('gamesPlayed')) | number:'1.0-2' }}</strong></th>
                  </tr>
                  <tr>
                    <th (click)="sortLiveStats('statDate')">Read</th>
                    <th (click)="sortLiveStats('assetTag')">Asset Tag</th>
                    <th (click)="sortLiveStats('salesRep')">Sales Rep</th>
                    <th (click)="sortLiveStats('gameName')">Game Name</th>
                    <th (click)="sortLiveStats('location')">Location</th>
                    <th (click)="sortLiveStats('state')">State</th>
                    <th (click)="sortLiveStats('cashIn')">In</th>
                    <th (click)="sortLiveStats('cashOut')">Out</th>
                    <th (click)="sortLiveStats('net')">Net</th>
                    <th (click)="sortLiveStats('days')">Days</th>
                    <th (click)="sortLiveStats('cashInPerDay')">In/Day</th>
                    <th (click)="sortLiveStats('netPerDay')">Net/D</th>
                    <th (click)="sortLiveStats('payout')">Payout</th>
                    <th (click)="sortLiveStats('rpt')">RPT</th>
                    <th (click)="sortLiveStats('coinIn')">Turn</th>
                    <th (click)="sortLiveStats('coinOut')">Credit</th>
                    <th (click)="sortLiveStats('gamesPlayed')">Played</th>
                    <th (click)="sortLiveStats('gamesWon')">Won</th>
                    <th (click)="sortLiveStats('multiplier')">Mult</th>
                    <th (click)="sortLiveStats('bet')">Bet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of filteredFortuneLiveStats; trackBy: trackByLiveStat">
                    <td>{{ item.statDate | date:'MMM d' }}</td>
                    <td>
                      <a href="javascript:void(0);" (click)="openBoardStatsModal(item.assetTag)">
                        {{ item.assetTag || '-' }}
                      </a>
                    </td>
                    <td>{{ item.salesRepName || '-' }}</td>
                    <td>{{ getFortuneGameName(item) }}</td>
                    <td>{{ item.locationName || '-' }}</td>
                    <td>{{ getFortuneState(item) }}</td>
                    <td>{{ numberValue(item.cashIn).toFixed(0) }}</td>
                    <td>{{ numberValue(item.cashOut).toFixed(0) }}</td>
                    <td>{{ getFortuneNet(item).toFixed(0) }}</td>
                    <td>1</td>
                    <td>{{ numberValue(item.cashIn).toFixed(0) }}</td>
                    <td>{{ getFortuneNet(item).toFixed(0) }}</td>
                    <td>{{ getFortunePayout(item) | number:'1.0-2' }}%</td>
                    <td>{{ getFortuneRpt(item) | number:'1.0-0' }}%</td>
                    <td>{{ numberValue(item.coinIn).toFixed(0) }}</td>
                    <td>{{ numberValue(item.coinOut).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gamesPlayed).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gamesWon).toFixed(0) }}</td>
                    <td>{{ getFortuneBetMultiplier(item).toFixed(2) }}</td>
                    <td>{{ getFortuneBetPerPlay(item).toFixed(2) }}</td>
                  </tr>
                  <tr *ngIf="filteredFortuneLiveStats.length === 0">
                    <td colspan="20" class="text-center text-muted py-4">No field live stats found for the selected period.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row" *ngIf="view === 'operations' && flaggedStats.length > 0">
      <div class="col-12">
        <div class="card analytics-card table-card flagged-stats-card">
          <div class="card-body">
            <h4 class="card-title mb-2">Flagged Board Stats</h4>
            <div class="text-muted small mb-3">
              These rows are excluded from Board Stats Analysis totals and averages.
            </div>
            <div class="stats-mobile-summary flagged">
              <table class="table stats-summary-table">
                <thead>
                  <tr>
                    <th>Summary</th>
                    <th class="text-end">Boards</th>
                    <th class="text-end">Cash In</th>
                    <th class="text-end">Net</th>
                    <th class="text-end">Cash In/Day</th>
                    <th class="text-end">Net/Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Flagged Totals</td>
                    <td class="text-end">{{ flaggedStats.length }}</td>
                    <td class="text-end">{{ flaggedStats | sum:'cashInDiff' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ flaggedStats | sum:'profit' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ flaggedStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</td>
                    <td class="text-end">{{ flaggedStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="stats-mobile-sort">
              <label for="flaggedStatsSort">Sort by</label>
              <select id="flaggedStatsSort" class="form-select form-select-sm" [value]="boardStatsSortColumn" (change)="setBoardStatsSort($any($event.target).value)">
                <option *ngFor="let option of boardStatsSortOptions" [value]="option.value">{{ option.label }}</option>
              </select>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleBoardStatsSortDirection()">
                {{ boardStatsSortAscending ? 'Asc' : 'Desc' }}
              </button>
            </div>
            <div class="table-scroll-controls">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(flaggedStatsScroll, -1)">
                Left
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="scrollStatsTable(flaggedStatsScroll, 1)">
                Right
              </button>
            </div>
            <div class="table-responsive breakdown-table" #flaggedStatsScroll>
              <table class="table table-bordered table-hover stats-table" style="min-width: 1200px;">
                <thead>
                  <tr class="table-warning totals-row">
                    <th class="sticky" colspan="6"><strong>Flagged Totals ({{ flaggedStats.length }} Boards)</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'cashInDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'cashOutDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'profit' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | average:'daysSincePrevRead' | number:'1.0-1' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | average:'cashInDiff/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | average:'profit/daysSincePrevRead' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ (flaggedStats | average:'holdPercentage') * 100 | number:'1.0-2' }}%</strong></th>
                    <th class="sticky"><strong>{{ ((flaggedStats | sum:'coinOutDiff') / (flaggedStats | sum:'coinInDiff')) * 100 | number:'1.0-0' }}%</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'coinInDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'coinOutDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'gamePlayedDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ flaggedStats | sum:'gameWonDiff' | number:'1.0-0' }}</strong></th>
                    <th class="sticky"><strong>{{ (flaggedStats | sum:'coinInDiff') / (flaggedStats | sum:'cashInDiff') | number:'1.0-2' }}</strong></th>
                    <th class="sticky"><strong>{{ (flaggedStats | sum:'coinInDiff') / (flaggedStats | sum:'gamePlayedDiff') | number:'1.0-2' }}</strong></th>
                  </tr>
                  <tr>
                    <th (click)="sortData('timeStamp')">Read</th>
                    <th (click)="sortData('assetTag')">Asset Tag</th>
                    <th (click)="sortData('salesRep')">Sales Rep</th>
                    <th (click)="sortData('gameName')">Game Name</th>
                    <th (click)="sortData('location')">Location</th>
                    <th (click)="sortData('state')">State</th>
                    <th (click)="sortData('cashIn')">In</th>
                    <th (click)="sortData('cashOut')">Out</th>
                    <th (click)="sortData('netRevenue')">Net</th>
                    <th (click)="sortData('daysSincePrevRead')">Days</th>
                    <th (click)="sortData('cashIn/day')">In/Day</th>
                    <th (click)="sortData('netRevenue/day')">Net/D</th>
                    <th (click)="sortData('payoutPercentage')">Payout</th>
                    <th (click)="sortData('rtp')">RPT</th>
                    <th (click)="sortData('coinIn')">Turn</th>
                    <th (click)="sortData('coinOut')">Credit</th>
                    <th (click)="sortData('gamesPlayed')">Played</th>
                    <th (click)="sortData('gamesWon')">Won</th>
                    <th>Mult</th>
                    <th>Bet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of flaggedStats; trackBy: trackByStat" class="stats-row table-warning">
                    <td>{{ item.timeStamp | date:'MMM d' }}</td>
                    <td>
                      <a href="javascript:void(0);" (click)="openBoardStatsModal(item.board?.assetTag)">
                        {{ item.board?.assetTag }}
                      </a>
                    </td>
                    <td>{{ item.__salesRepName }}</td>
                    <td>
                      {{ item.board?.currentDeployment?.installedGamePackage?.name || item.board?.installedGamePackage?.name }}
                      <div *ngIf="item.flaggedNote" class="text-danger small">Flagged: {{ item.flaggedNote }}</div>
                    </td>
                    <td>{{ item.__locationName }}</td>
                    <td>{{ item.__state }}</td>
                    <td>{{ numberValue(item.cashInDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.cashOutDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.profit).toFixed(0) }}</td>
                    <td>{{ item.daysSincePrevRead }}</td>
                    <td>{{ perDay(item.cashInDiff, item.daysSincePrevRead).toFixed(0) }}</td>
                    <td>{{ perDay(item.profit, item.daysSincePrevRead).toFixed(0) }}</td>
                    <td>{{ numberValue(item.holdPercentage) * 100 | number:'1.0-2' }}%</td>
                    <td>{{ percent(item.coinOutDiff, item.coinInDiff) | number:'1.0-0' }}%</td>
                    <td>{{ numberValue(item.coinInDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.coinOutDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gamePlayedDiff).toFixed(0) }}</td>
                    <td>{{ numberValue(item.gameWonDiff).toFixed(0) }}</td>
                    <td>{{ ratio(item.coinInDiff, item.cashInDiff).toFixed(2) }}</td>
                    <td>{{ ratio(item.coinInDiff, item.gamePlayedDiff).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

  </ng-container>
</div>
  `,
  styleUrls: ['./internal-dashboard.component.scss']
})
export class InternalDashboardComponent implements OnInit {
  view: InternalView = 'overview';
  loading = true;
  loadError = '';
  dataWarnings: string[] = [];
  availableYears: number[] = [];
  monthOptions = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' }
  ];
  selectedYears: number[] = [];
  selectedMonths: number[] = [];
  selectedStartDate: Date = new Date();
  selectedEndDate: Date = new Date();
  customStartInput = '';
  customEndInput = '';
  activeRangeLabel = '';
  dateFilterExpanded = false;

  locations: Location[] = [];
  currentMonthSales: Location[] = [];
  cabinetSaleLocations: Location[] = [];
  boardSaleLocations: Location[] = [];
  boardStatsRows: InternalBoardStat[] = [];
  flaggedBoardStatsRows: InternalBoardStat[] = [];
  leaseBoardStatsRows: InternalBoardStat[] = [];
  flaggedLeaseBoardStatsRows: InternalBoardStat[] = [];
  fortuneLiveStats: FusionConnectDailyStat[] = [];
  collectionSummaries: CollectionSummaryDTO[] = [];
  salesReps: SalesRep[] = [];
  leaseReps: SalesRep[] = [];
  selectedLeaseSalesRepName = '';
  leads: LeadDTO[] = [];
  selectedLead: LeadDTO | null = null;
  showLeadModal = false;
  inventory: InternalInventory = { boards: 0, cabinets: 0, kiosks: 0 };
  private loadSequence = 0;

  cashInPerDay = 0;
  netPerDay = 0;
  daysSincePrevDisplay = 0;
  boardStatsSortColumn = 'timeStamp';
  boardStatsSortAscending = false;
  boardStatsGameFilter = '';
  boardStatsSalesRepFilter = '';
  boardStatsStateFilter = '';
  showLiveStats = false;
  liveStatsSortColumn = 'statDate';
  liveStatsSortAscending = false;
  liveStatsLoaded = false;
  boardStatsSortOptions = [
    { value: 'timeStamp', label: 'Read Date' },
    { value: 'assetTag', label: 'Asset Tag' },
    { value: 'location', label: 'Location' },
    { value: 'salesRep', label: 'Sales Rep' },
    { value: 'state', label: 'State' },
    { value: 'gameName', label: 'Game Name' },
    { value: 'cashIn', label: 'Cash In' },
    { value: 'cashOut', label: 'Cash Out' },
    { value: 'netRevenue', label: 'Net Revenue' },
    { value: 'daysSincePrevRead', label: 'Days Since Last' },
    { value: 'cashIn/day', label: 'Cash In/Day' },
    { value: 'netRevenue/day', label: 'Net/Day' },
    { value: 'payoutPercentage', label: 'Payout %' },
    { value: 'rtp', label: 'RTP' },
    { value: 'coinIn', label: 'Turnover' },
    { value: 'coinOut', label: 'Credit Won' },
    { value: 'gamesPlayed', label: 'Games Played' },
    { value: 'gamesWon', label: 'Games Won' }
  ];
  collectedLocationCount = 0;
  collectedGross = 0;
  collectedNet = 0;
  leaseMonthlyRevenue = 0;
  leaseBoards = 0;
  daysInCurrentMonth = 31;
  private readonly defaultSalesOutletUnitPrice = 1500;
  private readonly moveToFusionLeaseNote = '__MOVE_TO_FUSION_LEASE__';
  private readonly moveToPickupNote = '__MOVE_TO_PICKUP__';
  private readonly fusionLeaseSalesReps = [
    'Nestor Sanchez',
    'Andy Vasvani',
    'Amin Popatiya',
    'RAFIQ MERCHANT',
    'Nahinur Rahman',
    'SULEMAN SUKHIANY',
    'Peter Patel'
  ];
  private readonly pickupLocations = [
    'Team Azad',
    'Rocky Virgina',
    'AJU TEAM',
    'Team Azad TN',
    'RO',
    'ROCKY TN',
    'Team AJU TN'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private collectionService: CollectionService,
    private salesRepService: SalesRepService,
    private leadService: LeadService,
    private boardStatsService: BoardStatService,
    private inventoryService: InternalInventoryService,
    private loginService: LoginService,
    private modalService: NgbModal,
    private fusionConnectService: FusionConnectService
  ) {}

  get isRoot(): boolean {
    return this.loginService.userType === UserType.ROOT;
  }

  getViewTitle(): string {
    if (this.view === 'overview') return 'Dashboard';
    if (this.view === 'operations') return 'Route Analysis';
    return this.view.charAt(0).toUpperCase() + this.view.slice(1);
  }

  async ngOnInit(): Promise<void> {
    this.initializePeriodOptions();
    this.route.paramMap.subscribe(params => {
      const nextView = (params.get('view') as InternalView) || 'overview';
      const viewChanged = this.view !== nextView;
      this.view = nextView;
      if (viewChanged) {
        this.showLiveStats = false;
      }
      void this.load();
    });
  }

  async load(): Promise<void> {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.loadError = '';
    this.dataWarnings = [];

    const { start, end } = this.selectedDateRange();
    const shouldLoadRouteAnalysis = this.view === 'operations';
    this.liveStatsLoaded = false;

    try {
      const [locationsResult, summariesResult, repsResult, statsSummaryResult, liveStatsResult, leadResult] = await Promise.allSettled([
        this.retryArrayLoad(() => this.loadLocationsForDashboard()),
        this.retryArrayLoad(() => this.fetchCollectionSummariesForRange(start, end)),
        this.retryArrayLoad(() => this.salesRepService.getAll()),
        this.retryValueLoad(
          () => this.loadRootBoardStatsMetrics(start, end),
          summary => this.hasUsableStatsSummary(summary)
        ),
        Promise.resolve([] as FusionConnectDailyStat[]),
        firstValueFrom(this.leadService.getAll())
      ]);

      if (sequence !== this.loadSequence) return;

      const locations = locationsResult.status === 'fulfilled' ? locationsResult.value : this.locations;
      const summaries = summariesResult.status === 'fulfilled' ? summariesResult.value : this.collectionSummaries;
      const reps = repsResult.status === 'fulfilled' ? repsResult.value : this.salesReps;
      const statsSummary = statsSummaryResult.status === 'fulfilled' ? statsSummaryResult.value : null;
      const liveStats = liveStatsResult.status === 'fulfilled' ? liveStatsResult.value : this.fortuneLiveStats;
      const leads = leadResult.status === 'fulfilled' ? leadResult.value : this.leads;

      if (locationsResult.status === 'rejected') this.addWarning('Sales/location data did not load.');
      if (summariesResult.status === 'rejected') this.addWarning('Collection summary data did not load.');
      if (repsResult.status === 'rejected') this.addWarning('Lease sales rep data did not load.');
      if (statsSummaryResult.status === 'rejected') this.addWarning('Cash in and net per day data did not load.');
      if (leadResult.status === 'rejected') this.addWarning('Lead data did not load.');

      this.locations = locations || this.locations || [];
      this.currentMonthSales = this.locations
        .filter(loc => this.isSalesOutlet(loc))
        .filter(loc => this.isDateInRange(loc.locationStartDate, start, end));
      this.cabinetSaleLocations = this.currentMonthSales
        .filter(loc => this.isCabinetSale(loc))
        .sort((a, b) => this.getLocationStartTimestamp(b) - this.getLocationStartTimestamp(a));
      this.boardSaleLocations = this.currentMonthSales
        .filter(loc => !this.isCabinetSale(loc))
        .sort((a, b) => this.getLocationStartTimestamp(b) - this.getLocationStartTimestamp(a));

      this.collectionSummaries = summaries || this.collectionSummaries || [];
      this.collectedLocationCount = new Set(this.collectionSummaries.map(s => s.locationName)).size;
      this.collectedGross = this.collectionSummaries.reduce((sum, s) => sum + (s.totalGross || 0), 0);
      this.collectedNet = this.collectionSummaries.reduce((sum, s) => sum + this.getCollectionNet(s), 0);

      this.salesReps = reps || this.salesReps || [];
      if (shouldLoadRouteAnalysis) {
        this.fortuneLiveStats = liveStats || this.fortuneLiveStats || [];
      }
      this.leads = leads || this.leads || [];
      this.leaseReps = (reps || []).filter(rep => rep.tenDollarEnabled || rep.fiveDollarEnabled);
      this.leaseBoards = this.leaseReps.reduce((sum, rep) => sum + (rep.numBoards || 0), 0);
      this.leaseMonthlyRevenue = this.leaseReps.reduce((sum, rep) => {
        const rate = rep.tenDollarEnabled ? 10 : rep.fiveDollarEnabled ? 5 : 0;
        return sum + rate * (rep.numBoards || 0) * this.daysInCurrentMonth;
      }, 0);

      if (statsSummary) {
        this.cashInPerDay = Number(statsSummary?.cashIn || 0);
        this.netPerDay = Number(statsSummary?.net || 0);
      }

      try {
        const inventory = await this.retryValueLoad(
          () => this.inventoryService.getCurrent(),
          value => this.getInventoryTotal(value) > 0
        );
        if (sequence !== this.loadSequence) return;
        this.inventory = inventory || this.inventory || { boards: 0, cabinets: 0, kiosks: 0 };
      } catch {
        this.inventory = this.inventory || { boards: 0, cabinets: 0, kiosks: 0 };
      }
    } catch (err) {
      console.error('Failed to load internal dashboard', err);
      if (sequence === this.loadSequence) {
        this.loadError = 'Dashboard data could not be loaded.';
      }
    } finally {
      if (sequence === this.loadSequence) {
        this.loading = false;
      }
    }
  }

  open(view: InternalView): void {
    void this.router.navigate(['/internal-dashboard', view]);
  }

  back(): void {
    void this.router.navigate(['/internal-dashboard']);
  }

  goToCollections(): void {
    void this.router.navigate(['/collectionSummary']);
  }

  goToInventory(): void {
    if (!this.isRoot) return;
    void this.router.navigate(['/inventory']);
  }

  goToBoards(): void {
    void this.router.navigate(['/boards']);
  }

  openLeadModal(): void {
    this.selectedLead = null;
    this.showLeadModal = true;
  }

  editLead(lead: LeadDTO): void {
    this.selectedLead = lead;
    this.showLeadModal = true;
  }

  async saveLead(lead: LeadDTO): Promise<void> {
    try {
      const saved = lead.id
        ? await firstValueFrom(this.leadService.update(lead.id, lead))
        : await firstValueFrom(this.leadService.create(lead));

      const existingIndex = this.leads.findIndex(item => item.id === saved.id);
      if (existingIndex >= 0) {
        this.leads = this.leads.map(item => item.id === saved.id ? saved : item);
      } else {
        this.leads = [...this.leads, saved];
      }

      this.showLeadModal = false;
      this.selectedLead = null;
    } catch (err) {
      console.error('Failed to save lead', err);
      this.addWarning('Lead could not be saved.');
    }
  }

  async deleteLead(id?: string): Promise<void> {
    if (!id) return;
    try {
      await firstValueFrom(this.leadService.delete(id));
      this.leads = this.leads.filter(lead => lead.id !== id);
    } catch (err) {
      console.error('Failed to delete lead', err);
      this.addWarning('Lead could not be deleted.');
    }
  }

  trackByLead(_index: number, lead: LeadDTO): string {
    return lead?.id || lead?.businessName || `${_index}`;
  }

  getLeadSalesRepName(id?: string): string {
    const rep = (this.salesReps || []).find(item => this.normalizeId(item.id) === this.normalizeId(id));
    return rep ? `${rep.firstName || ''} ${rep.lastName || ''}`.trim() : 'Potential New Sales Rep';
  }

  goToLegacyDashboard(queryParams: Record<string, string> = {}): void {
    void this.router.navigate(['/'], { queryParams });
  }

  private addWarning(message: string): void {
    if (!this.dataWarnings.includes(message)) {
      this.dataWarnings.push(message);
    }
  }

  isYearSelected(year: number): boolean {
    return this.selectedYears.includes(year);
  }

  isMonthSelected(month: number): boolean {
    return this.selectedMonths.includes(month);
  }

  isQuarterSelected(quarter: number): boolean {
    const months = this.monthsForQuarter(quarter);
    return months.every(month => this.selectedMonths.includes(month));
  }

  async toggleYear(year: number): Promise<void> {
    this.selectedYears = this.selectedYears.includes(year)
      ? this.selectedYears.filter(item => item !== year)
      : [...this.selectedYears, year].sort((a, b) => a - b);

    if (!this.selectedYears.length) {
      this.selectedYears = [new Date().getFullYear()];
    }

    this.applySelectedYearMonthRange();
    await this.load();
  }

  async toggleMonth(month: number): Promise<void> {
    this.selectedMonths = this.selectedMonths.includes(month)
      ? this.selectedMonths.filter(item => item !== month)
      : [...this.selectedMonths, month].sort((a, b) => a - b);

    this.applySelectedYearMonthRange();
    await this.load();
  }

  async toggleQuarter(quarter: number): Promise<void> {
    const months = this.monthsForQuarter(quarter);
    const selected = months.every(month => this.selectedMonths.includes(month));

    this.selectedMonths = selected
      ? this.selectedMonths.filter(month => !months.includes(month))
      : Array.from(new Set([...this.selectedMonths, ...months])).sort((a, b) => a - b);

    this.applySelectedYearMonthRange();
    await this.load();
  }

  onCustomStartChange(event: Event): void {
    this.customStartInput = (event.target as HTMLInputElement).value;
  }

  onCustomEndChange(event: Event): void {
    this.customEndInput = (event.target as HTMLInputElement).value;
  }

  async applyCustomRange(): Promise<void> {
    const start = this.parseDateInput(this.customStartInput);
    const end = this.parseDateInput(this.customEndInput);
    if (!start || !end) return;

    this.selectedStartDate = this.startOfDay(start);
    this.selectedEndDate = this.endOfDay(end);
    if (this.selectedStartDate > this.selectedEndDate) {
      const originalStart = this.selectedStartDate;
      this.selectedStartDate = this.startOfDay(this.selectedEndDate);
      this.selectedEndDate = this.endOfDay(originalStart);
    }

    this.syncSelectionFromRange();
    await this.load();
  }

  async setQuickRange(range: QuickRange): Promise<void> {
    const today = new Date();
    const end = this.endOfDay(today);
    let start: Date;

    switch (range) {
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = this.startOfDay(yesterday);
        this.selectedEndDate = this.endOfDay(yesterday);
        break;
      }
      case 'last7':
        start = this.startOfDay(this.addDays(today, -6));
        this.selectedEndDate = end;
        break;
      case 'last30':
        start = this.startOfDay(this.addDays(today, -29));
        this.selectedEndDate = end;
        break;
      case 'last3m':
        start = this.startOfDay(new Date(today.getFullYear(), today.getMonth() - 2, 1));
        this.selectedEndDate = end;
        break;
      case 'last12m':
        start = this.startOfDay(new Date(today.getFullYear(), today.getMonth() - 11, 1));
        this.selectedEndDate = end;
        break;
      case 'ytd':
        start = this.startOfDay(new Date(today.getFullYear(), 0, 1));
        this.selectedEndDate = end;
        break;
      case 'all':
        start = this.startOfDay(new Date(2020, 0, 1));
        this.selectedEndDate = end;
        break;
      default:
        start = this.startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
        this.selectedEndDate = end;
    }

    this.selectedStartDate = start;
    this.syncSelectionFromRange();
    await this.load();
  }

  async resetPeriod(): Promise<void> {
    this.setCurrentMonthPeriod();
    await this.load();
  }

  get boardSalesCount(): number {
    return this.boardSaleLocations.reduce((sum, loc) => sum + (loc.numBoards || 0), 0);
  }

  get cabinetSalesCount(): number {
    return this.cabinetSaleLocations.reduce((sum, loc) => sum + (loc.numCabinets || loc.numBoards || 0), 0);
  }

  get currentMonthSalesTotal(): number {
    return this.currentMonthSales.reduce((sum, loc) => sum + this.getSalesOutletRowTotal(loc), 0);
  }

  get boardSalesTotal(): number {
    return this.boardSaleLocations.reduce((sum, loc) => sum + this.getSalesOutletRowTotal(loc), 0);
  }

  get cabinetSalesTotal(): number {
    return this.cabinetSaleLocations.reduce((sum, loc) => sum + this.getSalesOutletRowTotal(loc), 0);
  }

  get totalInventoryCount(): number {
    return this.txInventoryCount + this.gaInventoryCount + this.routeMachineCount;
  }

  get txInventoryCount(): number {
    return this.numberValue(this.inventory.txBoards) + this.numberValue(this.inventory.txCabinets) + this.numberValue(this.inventory.txKiosks);
  }

  get gaInventoryCount(): number {
    return this.numberValue(this.inventory.gaBoards) + this.numberValue(this.inventory.gaCabinets) + this.numberValue(this.inventory.gaKiosks);
  }

  get routeMachineCount(): number {
    const repsById = new Map((this.salesReps || []).map(rep => [this.normalizeId(rep.id), rep]));

    return (this.locations || []).reduce((sum, location) => {
      const salesRep = repsById.get(this.normalizeId(location.salesRep?.id)) || location.salesRep;
      if (!this.isFortuneRouteLocation(location, salesRep)) return sum;

      const state = this.normalizeState(location?.businessAddress?.state) || 'Unknown';
      if (this.isExcludedRouteState(state)) return sum;

      return sum + this.getLocationMachineCount(location);
    }, 0);
  }

  get currentMonthLeads(): LeadDTO[] {
    return (this.leads || []).filter(lead => this.isCurrentMonthLead(lead));
  }

  get recentCurrentMonthLeads(): LeadDTO[] {
    return [...this.currentMonthLeads]
      .sort((a, b) => this.getLeadTimestamp(b) - this.getLeadTimestamp(a))
      .slice(0, 2);
  }

  get validStats(): InternalBoardStat[] {
    return this.filterBoardStats(this.boardStatsRows);
  }

  get flaggedStats(): InternalBoardStat[] {
    return this.filterBoardStats(this.flaggedBoardStatsRows);
  }

  get selectedLeaseRepStats(): InternalBoardStat[] {
    const selected = this.normalizeName(this.selectedLeaseSalesRepName);
    if (!selected) return [];
    return this.leaseBoardStatsRows.filter(stat => this.normalizeName(stat.__salesRepName) === selected);
  }

  get selectedLeaseRepAverageDays(): number {
    const rows = this.selectedLeaseRepStats;
    if (!rows.length) return 0;
    return rows.reduce((sum, stat) => sum + this.numberValue(stat.daysSincePrevRead), 0) / rows.length;
  }

  get boardStatsGameOptions(): string[] {
    return this.uniqueBoardStatsValues(stat => this.getBoardStatsGameName(stat));
  }

  get boardStatsSalesRepOptions(): string[] {
    return this.uniqueBoardStatsValues(stat => stat.__salesRepName || 'Unknown');
  }

  get boardStatsStateOptions(): string[] {
    return this.uniqueBoardStatsValues(stat => stat.__state || '');
  }

  sortData(column: string): void {
    if (this.boardStatsSortColumn === column) {
      this.boardStatsSortAscending = !this.boardStatsSortAscending;
    } else {
      this.boardStatsSortColumn = column;
      this.boardStatsSortAscending = true;
    }

    this.boardStatsRows = this.sortBoardStats(this.boardStatsRows);
    this.flaggedBoardStatsRows = this.sortBoardStats(this.flaggedBoardStatsRows);
    this.leaseBoardStatsRows = this.sortBoardStats(this.leaseBoardStatsRows);
    this.flaggedLeaseBoardStatsRows = this.sortBoardStats(this.flaggedLeaseBoardStatsRows);
  }

  setBoardStatsSort(column: string): void {
    this.boardStatsSortColumn = column;
    this.boardStatsRows = this.sortBoardStats(this.boardStatsRows);
    this.flaggedBoardStatsRows = this.sortBoardStats(this.flaggedBoardStatsRows);
    this.leaseBoardStatsRows = this.sortBoardStats(this.leaseBoardStatsRows);
    this.flaggedLeaseBoardStatsRows = this.sortBoardStats(this.flaggedLeaseBoardStatsRows);
  }

  toggleBoardStatsSortDirection(): void {
    this.boardStatsSortAscending = !this.boardStatsSortAscending;
    this.boardStatsRows = this.sortBoardStats(this.boardStatsRows);
    this.flaggedBoardStatsRows = this.sortBoardStats(this.flaggedBoardStatsRows);
    this.leaseBoardStatsRows = this.sortBoardStats(this.leaseBoardStatsRows);
    this.flaggedLeaseBoardStatsRows = this.sortBoardStats(this.flaggedLeaseBoardStatsRows);
  }

  openBoardStatsModal(assetTag?: string | null): void {
    if (!assetTag) return;
    const modalRef = this.modalService.open(BoardStatsByIdComponent, {
      size: 'lg'
    });
    modalRef.componentInstance.assetTag = assetTag;
  }

  openLeaseRepStats(rep: SalesRep): void {
    this.selectedLeaseSalesRepName = this.getSalesRepName(rep);
    setTimeout(() => {
      document.querySelector('.lease-stats-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  clearLeaseRepStats(): void {
    this.selectedLeaseSalesRepName = '';
  }

  async toggleLiveStats(): Promise<void> {
    this.showLiveStats = !this.showLiveStats;
    if (this.showLiveStats) {
      if (!this.liveStatsLoaded) {
        const { start, end } = this.selectedDateRange();
        try {
          this.fortuneLiveStats = await this.loadFortuneLiveMetrics(start, end);
          this.liveStatsLoaded = true;
        } catch (err) {
          console.warn('Field live stats did not load', err);
          this.fortuneLiveStats = [];
          this.liveStatsLoaded = true;
          this.addWarning('Field live stats did not load.');
        }
      }
      setTimeout(() => {
        document.getElementById('internal-live-stats')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  scrollStatsTable(container: HTMLElement, direction: -1 | 1): void {
    if (!container) return;
    const distance = Math.max(220, Math.floor(container.clientWidth * 0.85));
    container.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  sortLiveStats(column: string): void {
    if (this.liveStatsSortColumn === column) {
      this.liveStatsSortAscending = !this.liveStatsSortAscending;
    } else {
      this.liveStatsSortColumn = column;
      this.liveStatsSortAscending = true;
    }
  }

  clearBoardStatsFilters(): void {
    this.boardStatsGameFilter = '';
    this.boardStatsSalesRepFilter = '';
    this.boardStatsStateFilter = '';
  }

  trackByStat(_index: number, item: InternalBoardStat): string {
    return item?.id || `${item?.board?.assetTag || ''}-${item?.timeStamp || ''}`;
  }

  trackByLiveStat(_index: number, item: FusionConnectDailyStat): string {
    return item?.id || `${item?.assetTag || ''}-${item?.statDate || ''}`;
  }

  get filteredFortuneLiveStats(): FusionConnectDailyStat[] {
    const rows = (this.fortuneLiveStats || [])
      .filter(item => !this.isFortuneWarehouseTestRow(item))
      .filter(item => !this.isFlaggedFortuneLiveStat(item))
      .filter(item => !this.boardStatsGameFilter || this.getFortuneGameName(item) === this.boardStatsGameFilter)
      .filter(item => !this.boardStatsSalesRepFilter || (item.salesRepName || '') === this.boardStatsSalesRepFilter)
      .filter(item => !this.boardStatsStateFilter || this.getFortuneState(item) === this.boardStatsStateFilter);

    return this.sortFortuneLiveStats(rows);
  }

  liveStatSum(key: 'cashIn' | 'cashOut' | 'net' | 'coinIn' | 'coinOut' | 'gamesPlayed' | 'gamesWon'): number {
    return this.filteredFortuneLiveStats.reduce((sum, item) => {
      if (key === 'net') return sum + this.getFortuneNet(item);
      return sum + this.numberValue((item as any)?.[key]);
    }, 0);
  }

  liveStatAverage(key: 'cashIn' | 'net'): number {
    const rows = this.filteredFortuneLiveStats;
    if (!rows.length) return 0;
    const total = key === 'net'
      ? rows.reduce((sum, item) => sum + this.getFortuneNet(item), 0)
      : rows.reduce((sum, item) => sum + this.numberValue(item.cashIn), 0);
    return total / rows.length;
  }

  getFortuneNet(item: FusionConnectDailyStat): number {
    return this.numberValue(item?.net ?? (this.numberValue(item?.cashIn) - this.numberValue(item?.cashOut)));
  }

  getFortuneState(item: FusionConnectDailyStat): string {
    const location = this.findLiveLocation(item);
    return this.normalizeState((location as any)?.businessAddress?.state || (item as any)?.state) || '-';
  }

  getFortuneGameName(item: FusionConnectDailyStat): string {
    return this.pickFirstString([
      item?.gamePackageName,
      item?.gamePackageVersion,
      (item as any)?.gameName
    ]) || '-';
  }

  getFortunePayout(item: FusionConnectDailyStat): number {
    return this.percent(item?.cashOut, item?.cashIn);
  }

  getFortuneRpt(item: FusionConnectDailyStat): number {
    return this.percent(item?.coinOut, item?.coinIn);
  }

  getFortuneBetMultiplier(item: FusionConnectDailyStat): number {
    return this.ratio(item?.coinIn, item?.cashIn);
  }

  getFortuneBetPerPlay(item: FusionConnectDailyStat): number {
    return this.ratio(item?.coinIn, item?.gamesPlayed);
  }

  private sortFortuneLiveStats(rows: FusionConnectDailyStat[]): FusionConnectDailyStat[] {
    return [...rows].sort((a, b) => {
      const valueA = this.getFortuneLiveSortValue(a, this.liveStatsSortColumn);
      const valueB = this.getFortuneLiveSortValue(b, this.liveStatsSortColumn);

      if (valueA < valueB) return this.liveStatsSortAscending ? -1 : 1;
      if (valueA > valueB) return this.liveStatsSortAscending ? 1 : -1;
      return 0;
    });
  }

  private getFortuneLiveSortValue(item: FusionConnectDailyStat, column: string): any {
    switch (column) {
      case 'statDate': return item?.statDate || '';
      case 'assetTag': return item?.assetTag || '';
      case 'salesRep': return item?.salesRepName || '';
      case 'gameName': return this.getFortuneGameName(item);
      case 'location': return item?.locationName || '';
      case 'state': return this.getFortuneState(item);
      case 'cashIn':
      case 'cashInPerDay': return this.numberValue(item?.cashIn);
      case 'cashOut': return this.numberValue(item?.cashOut);
      case 'net':
      case 'netPerDay': return this.getFortuneNet(item);
      case 'days': return 1;
      case 'payout': return this.getFortunePayout(item);
      case 'rpt': return this.getFortuneRpt(item);
      case 'coinIn': return this.numberValue(item?.coinIn);
      case 'coinOut': return this.numberValue(item?.coinOut);
      case 'gamesPlayed': return this.numberValue(item?.gamesPlayed);
      case 'gamesWon': return this.numberValue(item?.gamesWon);
      case 'multiplier': return this.getFortuneBetMultiplier(item);
      case 'bet': return this.getFortuneBetPerPlay(item);
      default: return '';
    }
  }

  numberValue(value: any): number {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  perDay(value: any, days: any): number {
    const dayCount = this.numberValue(days);
    return dayCount > 0 ? this.numberValue(value) / dayCount : 0;
  }

  ratio(numerator: any, denominator: any): number {
    const bottom = this.numberValue(denominator);
    return bottom ? this.numberValue(numerator) / bottom : 0;
  }

  percent(numerator: any, denominator: any): number {
    return this.ratio(numerator, denominator) * 100;
  }

  getSalesOutletRowTotal(loc: Location): number {
    return this.getSalesOutletUnits(loc) * this.getSalesOutletUnitPrice(loc);
  }

  getSalesOutletUnits(loc: Location): number {
    return this.isCabinetSale(loc)
      ? Number(loc.numCabinets || loc.numBoards || 0)
      : Number(loc.numBoards || 0);
  }

  getSalesOutletUnitPrice(loc: Location): number {
    const candidate =
      this.parsePrice((loc as any)?.salesOutletBoardPrice) ??
      this.parsePrice((loc as any)?.customBoardPrice) ??
      this.parsePrice((loc as any)?.boardPrice);

    return candidate ?? this.defaultSalesOutletUnitPrice;
  }

  getLocationState(loc: Location): string {
    return this.normalizeState(loc?.businessAddress?.state) || 'Unknown';
  }

  private isSalesOutlet(loc: Location): boolean {
    const value = loc?.salesOutlet;
    return value === true || value === 1 || String(value).toLowerCase() === '1' || String(value).toLowerCase() === 'true';
  }

  private isCabinetSale(loc: Location): boolean {
    return (loc?.locationName || '').toLowerCase().includes('cab');
  }

  private isFortuneRouteLocation(location: Location, salesRep?: SalesRep): boolean {
    if (!location || this.isSalesOutlet(location) || this.isExplicitlyFalse(location.enabled)) return false;
    if (this.isWarehouseOrRepairLocation(location, salesRep)) return false;
    return !this.hasLeaseFlag(location) && !this.hasLeaseFlag(salesRep);
  }

  private getLocationMachineCount(location: Location): number {
    return Math.max(0, this.numberValue(location?.numBoards || location?.numCabinets || 0));
  }

  private normalizeState(value?: string | null): string {
    const state = (value || '').trim().toUpperCase();
    if (state === 'TEXAS') return 'TX';
    if (state === 'GEORGIA') return 'GA';
    if (state === 'PUERTO RICO') return 'PR';
    if (state === 'WISCONSIN') return 'WI';
    return state;
  }

  private isExcludedRouteState(state: string): boolean {
    return state === 'PR' || state === 'WI' || state === 'Unknown';
  }

  private isExplicitlyFalse(value: unknown): boolean {
    return value === false || value === 0 || String(value).toLowerCase() === '0' || String(value).toLowerCase() === 'false';
  }

  private hasLeaseFlag(value: unknown): boolean {
    const source = value as any;
    return this.isTruthy(source?.tenDollarEnabled) ||
      this.isTruthy(source?.fiveDollarEnabled) ||
      this.isTruthy(source?.is10ADay) ||
      this.isTruthy(source?.is10aDay) ||
      this.isTruthy(source?.is10aday) ||
      this.isTruthy(source?.tenDollar) ||
      this.isTruthy(source?.is5ADay) ||
      this.isTruthy(source?.is5aDay) ||
      this.isTruthy(source?.is5aday) ||
      this.isTruthy(source?.fiveDollar);
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || String(value).toLowerCase() === '1' || String(value).toLowerCase() === 'true';
  }

  private isWarehouseOrRepairLocation(location: Location, salesRep?: SalesRep): boolean {
    return [
      location?.locationName,
      location?.locationTag,
      location?.businessAddress?.streetAddress1,
      location?.businessAddress?.streetAddress2,
      location?.businessAddress?.city,
      this.getSalesRepName(salesRep)
    ].some(value => this.isWarehouseOrRepairText(value));
  }

  private isWarehouseOrRepairText(value?: string | null): boolean {
    const normalized = this.normalizeName(value);
    const compact = normalized.replace(/[^a-z0-9]/g, '');
    return normalized.includes('warehouse') ||
      normalized.includes('repair') ||
      normalized.includes('service') ||
      compact === 'wh' ||
      compact.includes('whse') ||
      compact.includes('warehouse') ||
      compact.includes('repair');
  }

  getSalesRepName(salesRep?: SalesRep | null): string {
    return `${salesRep?.firstName || ''} ${salesRep?.lastName || ''}`.trim() || 'Unassigned';
  }

  private isCurrentMonthLead(lead: LeadDTO): boolean {
    const rawDate = lead?.createdDate || lead?.expectedDeliveryDate || lead?.updatedDate;
    if (!rawDate) return false;

    const date = new Date(rawDate);
    if (!Number.isFinite(date.getTime())) return false;

    const today = new Date();
    return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
  }

  private getLeadTimestamp(lead: LeadDTO): number {
    const rawDate = lead?.createdDate || lead?.expectedDeliveryDate || lead?.updatedDate;
    const date = rawDate ? new Date(rawDate) : null;
    return date && Number.isFinite(date.getTime()) ? date.getTime() : 0;
  }

  private getLocationStartTimestamp(location: Location): number {
    const rawDate = location?.locationStartDate;
    const date = rawDate ? new Date(rawDate) : null;
    return date && Number.isFinite(date.getTime()) ? date.getTime() : 0;
  }

  private normalizeId(value?: string | null): string {
    return (value || '').toString().trim().toLowerCase();
  }

  private getCollectionNet(summary: CollectionSummaryDTO): number {
    return Number(summary?.totalGross || 0) * Number(summary?.splitPercentage || 0);
  }

  private async loadRootBoardStatsMetrics(start: Date, end: Date): Promise<{ cashIn: number; net: number }> {
    const allBoardStats = await this.boardStatsService.getAllByDateRange(start, end);
    const stats = (allBoardStats || [])
      .map(stat => this.buildInternalStatMeta(stat))
      .filter(stat => this.isRootDashboardCandidateStat(stat));

    this.leaseBoardStatsRows = this.sortBoardStats(stats.filter(stat => !this.isFlaggedStat(stat) && this.isFusionLeaseStat(stat)));
    this.flaggedLeaseBoardStatsRows = this.sortBoardStats(stats.filter(stat => this.isFlaggedStat(stat) && this.isFusionLeaseStat(stat)));
    this.boardStatsRows = this.sortBoardStats(stats.filter(stat => !this.isFlaggedStat(stat) && !this.isDedicatedBoardStat(stat)));
    this.flaggedBoardStatsRows = this.sortBoardStats(stats.filter(stat => this.isFlaggedStat(stat) && !this.isDedicatedBoardStat(stat)));

    let cashIn = 0;
    let net = 0;
    let count = 0;
    let totalDays = 0;

    for (const stat of this.boardStatsRows) {
      const days = Number(stat.daysSincePrevRead || 0);
      const cashInPerDay = Number(stat.cashInDiff || 0) / days;
      const netPerDay = Number(stat.profit || 0) / days;

      if (days > 0 && Number.isFinite(cashInPerDay) && Number.isFinite(netPerDay)) {
        cashIn += cashInPerDay;
        net += netPerDay;
        totalDays += days;
        count += 1;
      }
    }

    this.daysSincePrevDisplay = count ? totalDays / count : 0;
    return {
      cashIn: count ? cashIn / count : 0,
      net: count ? net / count : 0
    };
  }

  private async retryArrayLoad<T>(loader: () => Promise<T[] | undefined>, attempts = 4, delayMs = 350): Promise<T[]> {
    let lastResult: T[] = [];

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      const result = await loader();
      if (Array.isArray(result)) {
        lastResult = result;
        if (result.length > 0) return result;
      }

      if (attempt < attempts) {
        await this.delay(delayMs);
      }
    }

    return lastResult;
  }

  private async retryValueLoad<T>(
    loader: () => Promise<T | null | undefined>,
    isUsable: (value: T | null | undefined) => boolean,
    attempts = 4,
    delayMs = 350
  ): Promise<T | null | undefined> {
    let lastResult: T | null | undefined;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      lastResult = await loader();
      if (isUsable(lastResult)) return lastResult;

      if (attempt < attempts) {
        await this.delay(delayMs);
      }
    }

    return lastResult;
  }

  private hasUsableStatsSummary(summary?: { cashIn: number; net: number } | null): boolean {
    return !!summary && (
      this.numberValue(summary.cashIn) !== 0 ||
      this.numberValue(summary.net) !== 0 ||
      this.boardStatsRows.length > 0 ||
      this.leaseBoardStatsRows.length > 0
    );
  }

  private getInventoryTotal(inventory?: InternalInventory | null): number {
    if (!inventory) return 0;
    return this.numberValue(inventory.boards) +
      this.numberValue(inventory.cabinets) +
      this.numberValue(inventory.kiosks) +
      this.numberValue(inventory.gaBoards) +
      this.numberValue(inventory.txBoards) +
      this.numberValue(inventory.gaCabinets) +
      this.numberValue(inventory.txCabinets) +
      this.numberValue(inventory.gaKiosks) +
      this.numberValue(inventory.txKiosks);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async loadFortuneLiveMetrics(start: Date, end: Date): Promise<FusionConnectDailyStat[]> {
    const result = await this.fusionConnectService.fetchDailyStats({
      from: this.formatDate(start),
      to: this.formatDate(end),
      page: 0,
      pageSize: 2000
    });

    return ((result?.content || []) as FusionConnectDailyStat[])
      .filter(item => this.numberValue(item.cashIn) > 0 || this.getFortuneNet(item) !== 0)
      .sort((a, b) => (b.statDate || '').localeCompare(a.statDate || ''));
  }

  private findLiveLocation(item: FusionConnectDailyStat): Location | undefined {
    const locationId = this.normalizeId(item?.locationId || '');
    const locationName = (item?.locationName || '').trim().toLowerCase();

    return (this.locations || []).find(loc => {
      const candidateId = this.normalizeId(loc?.id || '');
      const candidateName = (loc?.locationName || '').trim().toLowerCase();
      return (!!locationId && candidateId === locationId) || (!!locationName && candidateName === locationName);
    });
  }

  private isFortuneWarehouseTestRow(item: FusionConnectDailyStat): boolean {
    const location = this.findLiveLocation(item);
    const searchable = [
      item?.locationName,
      item?.salesRepName,
      location?.locationName,
      location?.locationTag
    ].map(value => (value || '').toString().toLowerCase()).join(' ');

    return /\bwarehouse\b/.test(searchable) ||
      searchable.includes('test area') ||
      searchable.includes('board testing') ||
      searchable.includes('show room') ||
      searchable.includes('showroom');
  }

  private isFlaggedFortuneLiveStat(item: FusionConnectDailyStat): boolean {
    const value = item?.manuallyFlagged as any;
    return value === true || value === 1 || value === '1' || `${value}`.toLowerCase() === 'true';
  }

  private buildInternalStatMeta(stat: BoardStats): InternalBoardStat {
    const locationName = this.pickNonWarehouse([
      (stat as any)?.board?.location?.locationName,
      (stat as any)?.board?.currentDeployment?.location?.locationName,
      (stat as any)?.locationName,
      (stat as any)?.location
    ]);

    const salesRepName = this.pickNonWarehouse([
      `${(stat as any)?.board?.location?.salesRep?.firstName || ''} ${(stat as any)?.board?.location?.salesRep?.lastName || ''}`,
      `${(stat as any)?.board?.currentDeployment?.location?.salesRep?.firstName || ''} ${(stat as any)?.board?.currentDeployment?.location?.salesRep?.lastName || ''}`,
      (stat as any)?.salesRepName,
      (stat as any)?.salesRep
    ]);

    const firstLocationName = this.pickFirstString([
      (stat as any)?.board?.location?.locationName,
      (stat as any)?.board?.currentDeployment?.location?.locationName,
      (stat as any)?.locationName,
      (stat as any)?.location
    ]);
    const firstSalesRepName = this.pickFirstString([
      `${(stat as any)?.board?.location?.salesRep?.firstName || ''} ${(stat as any)?.board?.location?.salesRep?.lastName || ''}`,
      `${(stat as any)?.board?.currentDeployment?.location?.salesRep?.firstName || ''} ${(stat as any)?.board?.currentDeployment?.location?.salesRep?.lastName || ''}`,
      (stat as any)?.salesRepName,
      (stat as any)?.salesRep
    ]);
    const state = this.pickFirstString([
      (stat as any)?.board?.location?.businessAddress?.state,
      (stat as any)?.board?.currentDeployment?.location?.businessAddress?.state,
      (stat as any)?.state
    ]);

    return {
      ...stat,
      __locationName: locationName || firstLocationName || 'Unknown',
      __salesRepName: salesRepName || firstSalesRepName || 'Unknown',
      __state: state || '',
      __isWarehouse: this.isWarehouseName(locationName) ||
        this.isWarehouseName(salesRepName) ||
        this.isWarehouseName(firstLocationName) ||
        this.isWarehouseName(firstSalesRepName)
    };
  }

  private isRootDashboardCandidateStat(stat: InternalBoardStat): boolean {
    const firstName = (stat as any)?.board?.location?.salesRep?.firstName?.trim();
    const lastName = (stat as any)?.board?.location?.salesRep?.lastName?.trim();
    const blockedRep =
      (firstName === 'Shiraz' && lastName === 'Salim') ||
      (firstName === 'Vijay' && lastName === 'Patel');

    const hasValidRead =
      Number(stat.daysSincePrevRead || 0) > 0 &&
      Number(stat.daysSincePrevRead || 0) <= 70 &&
      Number(stat.cashInDiff || 0) > 0 &&
      Number(stat.cashOutDiff || 0) >= 0;

    return hasValidRead && !blockedRep && !stat.__isWarehouse;
  }

  private isFlaggedStat(stat: BoardStats): boolean {
    const value = stat?.manuallyFlagged as any;
    return value === true || value === 1 || value === '1' || `${value}`.toLowerCase() === 'true';
  }

  private isFusionLeaseStat(stat: InternalBoardStat): boolean {
    if (stat.statsCategory === 'STANDARD' ||
        stat.statsCategory === 'PICKUP' ||
        stat.statsCategory === 'BOARD_SALES') return false;
    if (stat.statsCategory === 'FUSION_LEASE' || this.isMovedTo(stat, this.moveToFusionLeaseNote)) return true;

    const location = (stat as any)?.board?.currentDeployment?.location || (stat as any)?.board?.location;
    if (this.isSalesOutlet(location as Location)) return false;

    const repName = stat.__salesRepName ||
      `${location?.salesRep?.firstName || ''} ${location?.salesRep?.lastName || ''}`;
    const normalizedRep = this.normalizeName(repName);

    return this.fusionLeaseSalesReps.some(name => this.normalizeName(name) === normalizedRep);
  }

  private isDedicatedBoardStat(stat: InternalBoardStat): boolean {
    const category = stat?.statsCategory;
    if (category === 'FUSION_LEASE' || category === 'PICKUP' || category === 'BOARD_SALES') return true;
    if (this.isMovedTo(stat, this.moveToFusionLeaseNote) || this.isMovedTo(stat, this.moveToPickupNote)) return true;

    const location = (stat as any)?.board?.currentDeployment?.location || (stat as any)?.board?.location;
    if (this.isSalesOutlet(location as Location)) return true;

    const locationName = stat.__locationName || location?.locationName || '';
    const repName = stat.__salesRepName || `${location?.salesRep?.firstName || ''} ${location?.salesRep?.lastName || ''}`;
    const normalizedLocation = this.normalizeName(locationName);
    const normalizedRep = this.normalizeName(repName);

    return normalizedRep === 'rahim visram' ||
      this.fusionLeaseSalesReps.some(name => this.normalizeName(name) === normalizedRep) ||
      this.pickupLocations.some(name => {
        const normalizedName = this.normalizeName(name);
        return normalizedName === normalizedRep || normalizedName === normalizedLocation;
      });
  }

  private isMovedTo(stat: BoardStats | null | undefined, destination: string): boolean {
    return (stat?.flaggedNote || '').trim() === destination;
  }

  private sortBoardStats(stats: InternalBoardStat[]): InternalBoardStat[] {
    const direction = this.boardStatsSortAscending ? 1 : -1;
    return [...(stats || [])].sort((a, b) => {
      const av = this.getBoardStatsSortValue(a, this.boardStatsSortColumn);
      const bv = this.getBoardStatsSortValue(b, this.boardStatsSortColumn);

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * direction;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * direction;
    });
  }

  private filterBoardStats(stats: InternalBoardStat[]): InternalBoardStat[] {
    return (stats || []).filter(stat => {
      const gameName = this.getBoardStatsGameName(stat);
      const salesRep = stat.__salesRepName || 'Unknown';
      const state = stat.__state || '';

      return (!this.boardStatsGameFilter || gameName === this.boardStatsGameFilter) &&
        (!this.boardStatsSalesRepFilter || salesRep === this.boardStatsSalesRepFilter) &&
        (!this.boardStatsStateFilter || state === this.boardStatsStateFilter);
    });
  }

  private uniqueBoardStatsValues(selector: (stat: InternalBoardStat) => string): string[] {
    return Array.from(new Set([...this.boardStatsRows, ...this.flaggedBoardStatsRows]
      .map(selector)
      .filter(value => value !== null && value !== undefined)
      .map(value => String(value))))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private getBoardStatsGameName(item: InternalBoardStat): string {
    return (item as any)?.board?.currentDeployment?.installedGamePackage?.name ||
      item?.board?.installedGamePackage?.name ||
      'Unknown';
  }

  private getBoardStatsSortValue(item: InternalBoardStat, column: string): string | number | null {
    switch (column) {
      case 'timeStamp': return item?.timeStamp ? new Date(item.timeStamp).getTime() : null;
      case 'assetTag': return item?.board?.assetTag || '';
      case 'serialNumber': return item?.board?.serialNumber || '';
      case 'location': return item?.__locationName || '';
      case 'salesRep': return item?.__salesRepName || '';
      case 'state': return item?.__state || '';
      case 'gameName': return (item as any)?.board?.currentDeployment?.installedGamePackage?.name || item?.board?.installedGamePackage?.name || '';
      case 'cashIn': return this.numberValue(item?.cashInDiff);
      case 'cashOut': return this.numberValue(item?.cashOutDiff);
      case 'netRevenue': return this.numberValue(item?.profit);
      case 'daysSincePrevRead': return this.numberValue(item?.daysSincePrevRead);
      case 'cashIn/day': return this.perDay(item?.cashInDiff, item?.daysSincePrevRead);
      case 'netRevenue/day': return this.perDay(item?.profit, item?.daysSincePrevRead);
      case 'payoutPercentage': return this.numberValue(item?.holdPercentage);
      case 'rtp': return this.ratio(item?.coinOutDiff, item?.coinInDiff);
      case 'coinIn': return this.numberValue(item?.coinInDiff);
      case 'coinOut': return this.numberValue(item?.coinOutDiff);
      case 'gamesPlayed': return this.numberValue(item?.gamePlayedDiff);
      case 'gamesWon': return this.numberValue(item?.gameWonDiff);
      default: return '';
    }
  }

  private pickFirstString(list: Array<string | undefined | null>): string {
    return (list.find(v => !!(v && v.toString().trim())) || '').toString().trim();
  }

  private pickNonWarehouse(list: Array<string | undefined | null>): string | undefined {
    return list
      .map(v => (v || '').toString().trim())
      .find(v => v && !this.isWarehouseName(v));
  }

  private isWarehouseName(value?: string | null): boolean {
    return !!value && value.toString().toLowerCase().includes('warehouse');
  }

  private normalizeName(value?: string | null): string {
    return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private initializePeriodOptions(): void {
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    for (let year = 2020; year <= currentYear; year += 1) {
      this.availableYears.push(year);
    }

    this.setCurrentMonthPeriod();
  }

  private setCurrentMonthPeriod(): void {
    const now = new Date();
    this.selectedStartDate = this.startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    this.selectedEndDate = this.endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    this.selectedYears = [now.getFullYear()];
    this.selectedMonths = [now.getMonth() + 1];
    this.updatePeriodMetadata();
  }

  private applySelectedYearMonthRange(): void {
    const years = this.selectedYears.length ? this.selectedYears : [new Date().getFullYear()];
    const months = this.selectedMonths.length ? this.selectedMonths : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    const starts = years.flatMap(year => months.map(month => new Date(year, month - 1, 1)));
    const ends = years.flatMap(year => months.map(month => new Date(year, month, 0)));
    const minStart = new Date(Math.min(...starts.map(date => date.getTime())));
    const maxEnd = new Date(Math.max(...ends.map(date => date.getTime())));

    this.selectedStartDate = this.startOfDay(minStart);
    this.selectedEndDate = this.endOfDay(maxEnd);
    this.updatePeriodMetadata();
  }

  private syncSelectionFromRange(): void {
    const years = new Set<number>();
    const months = new Set<number>();
    let cursor = new Date(this.selectedStartDate.getFullYear(), this.selectedStartDate.getMonth(), 1);
    const end = new Date(this.selectedEndDate.getFullYear(), this.selectedEndDate.getMonth(), 1);

    while (cursor <= end) {
      years.add(cursor.getFullYear());
      months.add(cursor.getMonth() + 1);
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    this.selectedYears = Array.from(years).sort((a, b) => a - b);
    this.selectedMonths = Array.from(months).sort((a, b) => a - b);
    this.updatePeriodMetadata();
  }

  private selectedDateRange(): { start: Date; end: Date } {
    this.updatePeriodMetadata();
    return {
      start: this.selectedStartDate,
      end: this.selectedEndDate
    };
  }

  private updatePeriodMetadata(): void {
    this.customStartInput = this.formatDate(this.selectedStartDate);
    this.customEndInput = this.formatDate(this.selectedEndDate);
    this.daysInCurrentMonth = this.daysBetweenInclusive(this.selectedStartDate, this.selectedEndDate);
    this.activeRangeLabel = `${this.daysInCurrentMonth} day${this.daysInCurrentMonth === 1 ? '' : 's'} - ${this.formatShortDate(this.selectedStartDate)} - ${this.formatShortDate(this.selectedEndDate)}`;
  }

  private async fetchCollectionSummariesForRange(start: Date, end: Date): Promise<CollectionSummaryDTO[]> {
    const selectedMonths = this.monthTagsBetween(start, end);
    const all: CollectionSummaryDTO[] = [];
    const seen = new Set<string>();
    const pageSize = 100;

    for (const month of selectedMonths) {
      let page = 0;

      while (page < 100) {
        const items = await firstValueFrom(this.collectionService.getSummariesPage(page, pageSize, month));

        for (const item of items || []) {
          const key = item.id || `${item.locationName || ''}|${item.collectionMonth || ''}|${item.currentDate || ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            all.push(item);
          }
        }

        if (!items || items.length < pageSize) {
          break;
        }

        page += 1;
      }
    }

    return all.filter(summary => selectedMonths.includes(summary.collectionMonth));
  }

  private async loadLocationsForDashboard(): Promise<Location[]> {
    const attempts = [
      () => this.locationService.getAll(true),
      () => this.locationService.getAll(false),
      () => this.locationService.getLatest()
    ];

    for (const attempt of attempts) {
      try {
        const locations = await attempt();
        if (Array.isArray(locations)) {
          return locations;
        }
      } catch (err) {
        console.warn('Internal dashboard location load attempt failed', err);
      }
    }

    throw new Error('Unable to load locations for internal dashboard');
  }

  private monthTagsBetween(start: Date, end: Date): string[] {
    const tags: string[] = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= last) {
      tags.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    return tags;
  }

  private monthsForQuarter(quarter: number): number[] {
    const start = (quarter - 1) * 3 + 1;
    return [start, start + 1, start + 2];
  }

  private parseDateInput(value: string): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
  }

  private addDays(date: Date, days: number): Date {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
  }

  private daysBetweenInclusive(start: Date, end: Date): number {
    const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.max(1, Math.floor((endUtc - startUtc) / 86400000) + 1);
  }

  private isDateInRange(value: string, start: Date, end: Date): boolean {
    if (!value) return false;
    const date = new Date(value);
    return !isNaN(date.getTime()) && date >= start && date <= end;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatShortDate(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  }

  private parsePrice(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

}
